import { lower } from "case";
import pluralize from "pluralize";
import React, { ReactElement, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { ListItem, ListItemText, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import SaveBackdrop from "../../../components/SaveBackdrop";
import { ErrorIcon } from "../../../components/icons";
import DeleteButton from "../../../components/styled/DeleteButton";
import { Policy } from "../../../store/pomeriumApi";

interface Props {
  open: boolean;
  entries: Policy[];
  onDelete(): Promise<void>;
  onClose(): void;
}

const PolicyDeleteActionDialog = ({
  open,
  entries,
  onDelete,
  onClose,
}: Props): ReactElement => {
  const [loading, setLoading] = useState(false);

  const handleClickConfirm = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    setLoading(true);
    onDelete().then(
      () => {
        onClose();
        setLoading(false);
      },
      () => {
        onClose();
        setLoading(false);
      },
    );
  };

  const handleClickCancel = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    onClose();
  };

  const enforcedPolicies = entries?.filter((p) => p.enforced);

  const lbl = pluralize("policy", entries?.length);
  return (
    <>
      <SaveBackdrop saving={loading} />
      <Dialog
        open={open && !loading}
        onClose={onClose}
        scroll="paper"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <ErrorIcon color="error" sx={{ mr: 1 }} />
          Delete {lbl}
          <IconButton
            color="primary"
            onClick={onClose}
            size="large"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack justifyContent="left" alignItems="left">
            <Typography variant="body1">
              {" "}
              {"Are you sure you want to delete"} the following{" "}
              {lower(pluralize("policy", entries?.length, false))}? This can't
              be undone.
            </Typography>
            <List dense disablePadding>
              {entries?.map((e) => (
                <ListItem key={"deleteDialog" + e?.id}>
                  <ListItemText>{e?.name || e?.id || ""}</ListItemText>
                </ListItem>
              ))}
            </List>
            {!!enforcedPolicies?.length && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1">
                  These policies will no longer be applied automatically:{" "}
                </Typography>
                <List dense disablePadding>
                  {enforcedPolicies?.map((e) => (
                    <ListItem key={"deleteEnforcedDialog" + e?.id}>
                      <ListItemText>{e?.name || e?.id || ""}</ListItemText>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickCancel}>Cancel</Button>
          <DeleteButton
            variant="contained"
            color="primary"
            onClick={handleClickConfirm}
          >
            Delete
          </DeleteButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default PolicyDeleteActionDialog;
